import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaSearch, FaTag, FaCheckCircle, FaUsers } from 'react-icons/fa';
import { useJobs } from '../context/JobsContext';
import ContactCTA from '../components/ContactCTA';
import '../styles/Careers.css';

const currencySymbols = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', AUD: 'A$', CAD: 'C$', CHF: 'CHF',
  CNY: '¥', INR: '₹', MXN: '$', BRL: 'R$', KRW: '₩', SGD: 'S$', HKD: 'HK$',
  NOK: 'kr', SEK: 'kr', DKK: 'kr', NZD: 'NZ$', ZAR: 'R', RUB: '₽', TRY: '₺',
  PLN: 'zł', THB: '฿', IDR: 'Rp', MYR: 'RM', PHP: '₱', CZK: 'Kč', ILS: '₪',
  CLP: '$', PKR: '₨', EGP: 'E£', KWD: 'KD', QAR: 'QR', SAR: 'SR', AED: 'AED',
  NGN: '₦', VND: '₫', BDT: '৳', ARS: '$', COP: '$', PEN: 'S/', UAH: '₴',
  RON: 'lei', HUF: 'Ft', BGN: 'лв', HRK: 'kn', ISK: 'kr', KES: 'KSh',
  GHS: 'GH₵', MAD: 'MAD', TWD: 'NT$', LKR: 'Rs', NPR: 'रू', JOD: 'JD',
  BHD: 'BD', OMR: 'OMR'
};

const formatPayRate = (payRate, currency) => {
  if (!payRate) return 'Competitive salary';
  const symbol = currencySymbols[currency] || currency || '$';
  return `${symbol}${parseFloat(payRate).toFixed(2)}/hr`;
};

const Careers = () => {
  const navigate = useNavigate();
  const { getActiveJobs, loading } = useJobs();
  const allJobs = getActiveJobs();

  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [filteredJobs, setFilteredJobs] = useState([]);

  // Update filtered jobs when allJobs changes
  useEffect(() => {
    setFilteredJobs(allJobs);
  }, [allJobs]);

  const handleSearch = (e) => {
    e.preventDefault();

    const keyword = searchKeyword.toLowerCase().trim();
    const location = searchLocation.toLowerCase().trim();

    const results = allJobs.filter((job) => {
      const matchesKeyword = keyword === '' ||
        (job.title && job.title.toLowerCase().includes(keyword)) ||
        (job.department && job.department.toLowerCase().includes(keyword)) ||
        (job.description && job.description.toLowerCase().includes(keyword));

      const matchesLocation = location === '' ||
        (job.location && job.location.toLowerCase().includes(location));

      return matchesKeyword && matchesLocation;
    });

    setFilteredJobs(results);
  };

  const handleClearSearch = () => {
    setSearchKeyword('');
    setSearchLocation('');
    setFilteredJobs(allJobs);
  };

  const handleApply = (jobId) => {
    navigate(`/careers/apply/${jobId}`);
  };

  return (
    <div className="careers-page">
      <section className="page-hero">
        <div className="container">
          <h1>Current Openings</h1>
          <p>Join Our Team and Shape the Future of Global Talent Solutions</p>
        </div>
      </section>

      <section className="careers-banner">
        <div className="container">
          <div className="careers-banner-grid">
            <img
              src="/images/careers-why-work.jpg"
              alt="Career opportunities at JWCS"
              className="careers-banner-img"
            />
            <img
              src="/images/careers-why-work-2.jpg"
              alt="Professional workplace"
              className="careers-banner-img"
            />
          </div>
        </div>
      </section>

      <section className="job-search">
        <div className="container">
          <form onSubmit={handleSearch} className="job-search-form">
            <div className="search-input-group">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Job title, keyword, or department"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="search-input-group">
              <FaMapMarkerAlt className="search-icon" />
              <input
                type="text"
                placeholder="Location"
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
                className="search-input"
              />
            </div>
            <button type="submit" className="btn btn-primary search-btn">
              <FaSearch /> Search Jobs
            </button>
            {(searchKeyword || searchLocation) && (
              <button type="button" className="btn btn-secondary clear-btn" onClick={handleClearSearch}>
                Clear
              </button>
            )}
          </form>
        </div>
      </section>

      <section className="job-openings">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Opportunities</span>
            <h2 className="section-title">Current Openings</h2>
            <p className="section-description">
              {loading
                ? 'Loading positions...'
                : filteredJobs.length !== allJobs.length
                  ? `Showing ${filteredJobs.length} matching positions`
                  : filteredJobs.length > 0
                    ? `${filteredJobs.length} positions available`
                    : 'Explore our available positions and find your next career opportunity'}
            </p>
          </div>

          {loading ? (
            <div className="loading-message">
              <p>Loading job listings...</p>
            </div>
          ) : filteredJobs.length > 0 ? (
            <div className="jobs-grid">
              {filteredJobs.map((job) => (
                <div className="job-card" key={job.id}>
                  <div className="job-card-image">
                    <img
                      src={job.image || '/images/job-placeholder.jpg'}
                      alt={job.title}
                    />
                    {job.vacancy && (
                      <div className="vacancy-badge">
                        <FaUsers /> {job.vacancy} {parseInt(job.vacancy) === 1 ? 'Vacancy' : 'Vacancies'}
                      </div>
                    )}
                  </div>
                  <div className="job-card-content">
                    <h3 className="job-card-title">{job.title}</h3>
                    <div className="job-card-details">
                      {job.payRate && (
                        <div className="job-card-detail">
                          <FaTag className="detail-icon salary-icon" />
                          <span>{formatPayRate(job.payRate, job.currency)}</span>
                        </div>
                      )}
                      {(job.workingHours || job.type) && (
                        <div className="job-card-detail">
                          <FaCheckCircle className="detail-icon hours-icon" />
                          <span>{job.workingHours || job.type}</span>
                        </div>
                      )}
                      {job.location && (
                        <div className="job-card-detail">
                          <FaMapMarkerAlt className="detail-icon location-icon" />
                          <span>{job.location}</span>
                        </div>
                      )}
                    </div>
                    <button
                      className="btn btn-success job-card-btn"
                      onClick={() => handleApply(job.id)}
                    >
                      Apply for this job
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-jobs-message">
              {searchKeyword || searchLocation ? (
                <>
                  <p>No jobs found matching your search criteria.</p>
                  <button className="btn btn-secondary" onClick={handleClearSearch}>
                    Clear Search
                  </button>
                </>
              ) : (
                <p>No positions are currently available. Please check back later for new opportunities.</p>
              )}
            </div>
          )}
        </div>
      </section>

      <ContactCTA />
    </div>
  );
};

export default Careers;
